import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  UNITS,
  ALL_LESSONS,
  lessonTileState,
} from "../lib/homeKeyCurriculum";
import { LESSON_MOMENTS, AUTHORED_LESSON_IDS } from "../lib/homeKeyLessons";
import {
  getUserId,
  subscribeProgress,
  emptyProgress,
} from "../lib/homeKeyProgress";
import "../styles/homekey.css";

/* ============================================================
   /home-key — the hub. A tile map of the whole course: every
   lesson is visible from day one. Done tiles show the keeper(s)
   made there, the current tile glows, coming-soon tiles are
   unlocked-but-unauthored, and locked tiles tease what's ahead
   ("your track gains: …"). The map doubles as a map of the
   track's construction.
   ============================================================ */

// Keeper names authored for a lesson (from its `try` moments).
const keeperNamesFor = (lessonId) =>
  (LESSON_MOMENTS[lessonId] || [])
    .filter((m) => m.type === "try" && m.keeperName)
    .map((m) => m.keeperName);

const Tile = ({ lesson, state, beginLabel }) => {
  const gainTag = (
    <span className="hk-tile-gain">your track gains: {lesson.gain}</span>
  );

  if (state === "done") {
    const names = keeperNamesFor(lesson.id);
    return (
      <Link
        to={`/home-key/lesson/${lesson.id}`}
        className="hk-tile hk-tile--done"
      >
        <span className="hk-tile-title">{lesson.title}</span>
        {names.length > 0 && (
          <span className="hk-tile-keepers">
            {names.map((name) => (
              <span key={name} className="hk-tile-keeper">
                ✦ {name}
              </span>
            ))}
          </span>
        )}
        <span className="hk-tile-cta">revisit</span>
      </Link>
    );
  }

  if (state === "current") {
    return (
      <Link
        to={`/home-key/lesson/${lesson.id}`}
        className="hk-tile hk-tile--current"
      >
        <span className="hk-tile-title">{lesson.title}</span>
        <span className="hk-tile-teaser">{lesson.teaser}</span>
        {gainTag}
        <span className="hk-tile-cta hk-tile-cta--glow">{beginLabel}</span>
      </Link>
    );
  }

  if (state === "coming-soon") {
    return (
      <div className="hk-tile hk-tile--coming-soon">
        <span className="hk-tile-title">{lesson.title}</span>
        <span className="hk-tile-teaser">{lesson.teaser}</span>
        {gainTag}
        <span className="hk-tile-badge">soon</span>
      </div>
    );
  }

  // Locked: dimmed and hazy, but the teaser stays legible — the promise of
  // what's ahead is the heart of the hub.
  return (
    <div className="hk-tile hk-tile--locked">
      <span className="hk-tile-title">{lesson.title}</span>
      <span className="hk-tile-teaser">{lesson.teaser}</span>
      {gainTag}
    </div>
  );
};

const HomeKey = () => {
  const [progress, setProgress] = useState(emptyProgress);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const unsub = subscribeProgress(getUserId(), (p) => {
      setProgress(p);
      setLoaded(true);
    });
    return unsub;
  }, []);

  const doneCount = ALL_LESSONS.filter((l) => progress.completed[l.id]).length;
  const keeperCount = Object.keys(progress.keepers).length;
  const beginLabel = doneCount === 0 ? "begin" : "continue";

  return (
    <div className="hk-root">
      <div className="hk-hub">
        <header className="hk-hub-header">
          <p className="hk-kicker">Home Key</p>
          <h1 className="hk-hub-title">Home Key</h1>
          <p className="hk-hub-subtitle">
            How music works, one feeling at a time. For this whole course, one
            note is home — G — and every melody, chord, and groove is a trip
            away from it and back.
          </p>
          <div className="hk-hub-meta">
            {loaded && (
              <p className="hk-progress-line">
                {doneCount} of {ALL_LESSONS.length} lessons · {keeperCount}{" "}
                {keeperCount === 1 ? "keeper" : "keepers"}
              </p>
            )}
            <Link to="/home-key/studio" className="hk-btn hk-btn--ghost">
              Your track · open the Studio
            </Link>
            <Link to="/home-key/practice" className="hk-btn hk-btn--ghost">
              Practice rooms · short sits, open anytime
            </Link>
          </div>
        </header>

        {UNITS.map((unit, i) => (
          <section key={unit.id} className="hk-unit-band">
            <header className="hk-unit-head">
              <p className="hk-unit-count">Unit {i + 1}</p>
              <h2 className="hk-unit-title">{unit.title}</h2>
              <p className="hk-unit-tagline">{unit.tagline}</p>
            </header>
            <div className="hk-tiles">
              {unit.lessons.map((lesson) => (
                <Tile
                  key={lesson.id}
                  lesson={lesson}
                  state={lessonTileState(
                    lesson.id,
                    progress.completed,
                    AUTHORED_LESSON_IDS
                  )}
                  beginLabel={beginLabel}
                />
              ))}
            </div>
          </section>
        ))}

        <footer className="hk-hub-footer">
          <p>
            Progress lives on this device (and quietly in the cloud). No
            scores, no streaks — just the music you keep.
          </p>
          <p>
            And when you feel like reps without a lesson, the{" "}
            <Link to="/home-key/practice">practice rooms</Link> are open after
            hours.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default HomeKey;
